import { IJobsiteSalesAreasRequest, IShipmentLocation, IShipmentLocationRequest } from '@cemex-core/cmx-api';
import { SHIPMENT_LOCATION_TYPE_ID } from '../../../../shared/constants/shipment-location-type-id';
import { DISTANCE_UNIT } from '../models/distance-unit.enum';

/**
 * @param {IShipmentLocation} shipmentLocation
 * @param {number} businessLineId
 * @returns IJobsiteSalesAreasRequest object used to GET the sales areas information for a jobsite
 */
export const prepareSalesAreaRequestParams = (
  shipmentLocation: IShipmentLocation,
  businessLineId: number,
): IJobsiteSalesAreasRequest => {
  return {
    shipmentLocationId: {
      entityId: shipmentLocation.shipmentLocationId,
      entityTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
    },
    productLineId: businessLineId,
  };
};

export const preparePointOfDeliveryByIdRequest = (
  shipmentLocation: IShipmentLocation,
  businessLine: number,
): IShipmentLocationRequest => {
  return {
    shipmentLocationId: {
      entityId: shipmentLocation.shipmentLocationId,
      entityTypeId: SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY,
    },
    shipmentLocationTypeId: [
      SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY,
      SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY_REQUEST,
    ].join(','),
    productLineId: businessLine,
  };
};

export function convertValueToMeters(value: number, unitCode: string): number {
  if (value < 1) {
    return value;
  }
  if (unitCode === DISTANCE_UNIT.KILOMETER) {
    return value / 1000;
  }
  if (unitCode === DISTANCE_UNIT.MILE) {
    return value * 1609.344;
  }
  return 0;
}

/**
 * Draws a circle on a Google Maps instance based on the given center and radius.
 *
 * @param {number} latitude - The latitude of the circle's center.
 * @param {number} longitude - The longitude of the circle's center.
 * @param {number} radius - The radius of the circle in meters.
 * @param {any} map - The Google Maps instance where the circle will be drawn.
 *
 * This function calculates the perimeter points of the circle using spherical geometry
 * and adds it as a polygon to the map. It also centers the map on the circle's center.
 *
 * @returns {void}
 */
export function drawCircleMap(latitude: number, longitude: number, radius: number, map) {
  try {
    const currentMarkerPosition = { lat: latitude, lng: longitude };
    map.mapInstance.setCenter(currentMarkerPosition);

    // build an object LatLng for the center of the circle
    const centerLatLng = new (window as any).google.maps.LatLng(currentMarkerPosition);

    // Calculate the points for the perimeter of the circle
    const latLngList = [];
    const numPoints = 360; // Number of points on the circle
    const angleStep = 360 / numPoints;
    const earthRadius = 6371000; // Earth radius in meters

    for (let i = 0; i < numPoints; i++) {
      const theta = i * angleStep * (Math.PI / 180); // Convert angle to radians
      const dx = radius * Math.cos(theta);
      const dy = radius * Math.sin(theta);

      // Calculate the new point
      const deltaLat = dy / earthRadius;
      const deltaLng = dx / (earthRadius * Math.cos((centerLatLng.lat() * Math.PI) / 180));

      const lat = centerLatLng.lat() + deltaLat * (180 / Math.PI);
      const lng = centerLatLng.lng() + deltaLng * (180 / Math.PI);

      latLngList.push(new (window as any).google.maps.LatLng(lat, lng));
    }

    new (window as any).google.maps.Polygon({
      strokeColor: '#003876',
      strokeOpacity: 1,
      strokeWeight: 2.5,
      fillOpacity: 0,
      map: map.mapInstance,
      paths: latLngList,
    });
    google.maps.event.trigger(map.mapInstance, 'resize');
  } catch (error) {
    console.log(error);
  }
}
